import _ from 'lodash';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import Playlist, { PlaylistItem } from './Playlist';

let _scriptLoadingPromise: Promise<void> | null = null;

export const ensureYoutubeApiReady = async (): Promise<void> => {
    if (window?.YT?.Player) {
        return;
    }

    if (_scriptLoadingPromise) {
        return _scriptLoadingPromise.then(() => {
            return;
        });
    }

    /*
     * 유튜브 IFrame API
     * @see https://developers.google.com/youtube/iframe_api_reference?hl=ko
     */
    _scriptLoadingPromise = new Promise((resolve) => {
        window.onYouTubeIframeAPIReady = function () {
            resolve(undefined);
        };

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';

        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
    });

    return _scriptLoadingPromise;
};

const PLAYER_KEY = 'youtube-playlist';

interface PlayerSnapshot {
    volume: number;
    isMuted: boolean;
    lastPlaylistItemId: string | null;
}

class PlayerStore {
    isInitialized = false;
    el: HTMLElement | null = null;
    player: YT.Player | null = null;
    /**
     * 플레이어 상태
     * -1은 실제 YT.PlayerState.UNSTARTED 값이지만
     * 스크립트가 로드되지 않았을 수 있으므로 직접 설정
     */
    playerState: YT.PlayerState = -1;
    playlist: Playlist = new Playlist();
    /**
     * 재생 중인 항목
     * 재생 중이거나 멈춤 상태일 때에만 할당된다
     */
    playingItem: PlaylistItem | null = null;
    /**
     * 재생 중인 인덱스
     */
    playingIndex: number = -1;
    /**
     * 마지막으로 재생한 플레이리스트 아이디
     * 재생 상태가 아니더라도 타이틀 등을 표시하기 위함
     */
    lastPlaylistItemId: string | null = null;

    /**
     * 현재 재생 중인 동영상의 길이
     */
    duration: number = 0;
    /**
     * 현재 재생 중인 시간
     */
    currentTime: number = 0;

    /**
     * 볼륨
     */
    volume: number = 100;
    isMuted = false;

    /**
     * 재생 시간 확인을 위한 타이머
     */
    timerId: number | undefined = undefined;

    /**
     * 복원 여부 (새로고침 이후 최초에 1회만 실행됨)
     */
    isRestored = false;

    debounceSave: () => void;

    constructor() {
        makeObservable(this, {
            player: observable,
            playerState: observable,
            playlist: observable,
            playingItem: observable,
            playingIndex: observable,
            lastPlaylistItemId: observable,
            duration: observable,
            currentTime: observable,
            volume: observable,
            isMuted: observable,

            isPlaying: computed,
            canPlay: computed,

            setPlayer: action,
            setPlayerState: action,
            addToPlaylist: action,
            playAtIndex: action,
            updateCurrentTime: action,
            setVolume: action,
            setMuted: action,
            restore: action,
            clear: action,
        });

        this.debounceSave = _.debounce(() => {
            this.save();
        }, 500);
    }

    async initialize(el: HTMLElement): Promise<void> {
        if (!el) {
            return;
        }

        if (this.el === el && this.isInitialized) {
            return;
        }

        await ensureYoutubeApiReady();
        this.clear();

        const options: YT.PlayerOptions = {
            width: '100%',
            height: '100%',
            events: {
                onReady: () => {
                    this.setPlayer(instance);

                    // 플레이어가 준비되면 스토어의 볼륨으로 복원한다
                    instance.setVolume(this.volume);
                    if (this.isMuted) {
                        instance.mute();
                    } else {
                        instance.unMute();
                    }
                },
                onStateChange: (e: YT.OnStateChangeEvent) => {
                    runInAction(() => {
                        this.setPlayerState(e.data);

                        if (e.data === YT.PlayerState.PLAYING) {
                            updateCurrentTimePeriodically();

                            // 플레이어에서 변경했을 수도 있으므로 재생되면 한 번 볼륨을 동기화해준다
                            if (this.player) {
                                this.setVolume(this.player.getVolume());
                                this.setMuted(this.player.isMuted());
                            }
                        } else {
                            clearTimeout(this.timerId);
                        }
                    });
                },
                onError: (e: YT.OnErrorEvent) => {
                    if (import.meta.env.DEV) {
                        console.log('ERROR:', e);
                    }
                },
            },
        };

        const updateCurrentTimePeriodically = () => {
            clearTimeout(this.timerId);
            runInAction(() => {
                this.updateCurrentTime();

                if (this.isPlaying) {
                    this.timerId = window.setTimeout(updateCurrentTimePeriodically, 1000);
                }
            });
        };

        const instance: YT.Player = new window.YT.Player(el, options);

        this.isInitialized = true;
    }

    play(): void {
        if (!this.player) {
            return;
        }

        if (this.playingItem) {
            this.player.playVideo();
        } else if (this.lastPlaylistItemId) {
            const index = this.playlist.getItemIndex(this.lastPlaylistItemId);
            if (index > -1) {
                this.playAtIndex(index);
            } else {
                this.playAtIndex(0);
            }
        } else {
            this.playAtIndex(0);
        }
    }

    playAtIndex(index: number, startTime = 0): void {
        if (!this.player || !this.canPlay) {
            return;
        }

        const playingItem = this.playlist.getByIndex(index);
        if (playingItem) {
            this.playingItem = playingItem;
            this.playingIndex = index;
            this.lastPlaylistItemId = playingItem.playlistItemId;

            this.player.loadVideoById(playingItem.videoId, startTime);
        }

        this.debounceSave();
    }

    seekTo(percent: number): void {
        if (this.player && _.isNumber(this.playingIndex) && this.duration) {
            const startTime = (this.duration * percent) / 100;
            this.playAtIndex(this.playingIndex, startTime);
        }
    }

    pause(): void {
        this.player?.pauseVideo();
    }

    playNext(): void {
        if (!this.canPlay) {
            return;
        }

        const nextIndex = this.playlist.getNextIndex(this.playingIndex);
        return this.playAtIndex(nextIndex);
    }

    playPrev(): void {
        if (!this.canPlay) {
            return;
        }

        const prevIndex = this.playlist.getPrevIndex(this.playingIndex);
        return this.playAtIndex(prevIndex);
    }

    setPlayer(player: YT.Player): void {
        this.player = player;
    }

    setPlayerState(playerState: YT.PlayerState): void {
        this.playerState = playerState;
        /*
            -1 –시작되지 않음
            0 – 종료
            1 – 재생 중
            2 – 일시중지
            3 – 버퍼링
            5 – 동영상 신호
        */
        if (this.playerState === YT.PlayerState.ENDED) {
            this.playNext();
        }
    }

    updateCurrentTime(): void {
        this.duration = this.player?.getDuration() || 0;
        this.currentTime = this.player?.getCurrentTime() || 0;
    }

    get isPlaying(): boolean {
        if (!this.player) {
            return false;
        }
        return this.playerState === YT.PlayerState.PLAYING;
    }

    get canPlay(): boolean {
        return !_.isEmpty(this.player) && !this.playlist.isEmpty;
    }

    getItemTitle(item: PlaylistItem | undefined | null): string {
        if (item) {
            return item.title;
        }
        return '';
    }

    /**
     * 플레이리스트에 항목을 추가하고 재생 상태를 업데이트한다
     * - 중복되는 항목은 제거
     * - 재생중일 떄 플레이어의 상태를 갱신하려면 명시적으로 load 해줘야 한다.
     * @returns {number} 추가된 마지막 항목의 인덱스
     */
    addToPlaylist(
        items: Omit<PlaylistItem, 'playlistItemId'> | Omit<PlaylistItem, 'playlistItemId'>[],
        {
            where,
            isPlayNow,
        }: {
            where: 'prepend' | 'append';
            /**
             * 바로 재생할 것인지
             */
            isPlayNow: boolean;
        } = {
            where: 'append',
            isPlayNow: false,
        },
    ): number {
        if (!this.player) {
            return -1;
        }

        if (!_.isArray(items)) {
            items = [items];
        }

        let index = -1;

        _.forEach(items, (item) => {
            const playlistItemId = Math.random().toString(36).substring(2, 15);
            if (where === 'prepend') {
                index = this.playlist.prepend({
                    ...item,
                    playlistItemId,
                });
            } else {
                index = this.playlist.append({
                    ...item,
                    playlistItemId,
                });
            }
        });

        if (isPlayNow) {
            this.playAtIndex(index);
        }

        return index;
    }

    removeFromPlaylist(item: PlaylistItem): void {
        // 현재 재생 중이라면 멈춘다
        if (this.playingItem?.playlistItemId === item.playlistItemId) {
            this.playingItem = null;
            this.playingIndex = -1;

            this.player?.stopVideo();
        }

        this.playlist.remove(item);

        this.debounceSave();
    }

    setVolume(volume: number): void {
        this.volume = volume;

        if (this.player) {
            this.player.setVolume(volume);
        }

        if (volume > 0) {
            this.setMuted(false);
        }

        this.debounceSave();
    }

    setMuted(isMuted: boolean): void {
        this.isMuted = isMuted;

        if (this.player) {
            if (isMuted) {
                this.player.mute();
            } else {
                this.player.unMute();
            }
        }

        this.debounceSave();
    }

    clear(): void {
        this.player?.destroy();
        this.player = null;
        this.el = null;
    }

    /**
     * 현재 플레이어의 상태를 로컬 스토리지에 저장한다
     */
    save(): void {
        if (!this.isRestored) {
            return;
        }

        const snapshot: PlayerSnapshot = {
            isMuted: this.isMuted,
            volume: this.volume,
            lastPlaylistItemId: this.lastPlaylistItemId,
        };
        localStorage.setItem(PLAYER_KEY, JSON.stringify(snapshot));
    }

    /**
     * 로컬 스토리지에서 플레이리스트를 복원한다
     * 생성된 이후 최초 1회만 실행된다
     */
    restore(): void {
        if (this.isRestored) {
            return;
        }

        const snapshot = localStorage.getItem(PLAYER_KEY);

        try {
            const restored: PlayerSnapshot = JSON.parse(snapshot || '{}');
            if (!_.isEmpty(restored)) {
                this.volume = restored.volume;
                this.isMuted = restored.isMuted;
                this.lastPlaylistItemId = restored.lastPlaylistItemId;
            }
        } catch (e) {
            // ignore
        }

        this.isRestored = true;
    }
}

export default new PlayerStore();
